/**
 * Cloud-side data operations for the current signed-in user.
 *
 * Used by Settings when the user requests "Delete all data", "Reset
 * placement", or "Delete account" — those actions must wipe Supabase rows
 * in addition to local IndexedDB, otherwise the sync engine repopulates
 * local state on the next pull.
 *
 * All functions are no-ops when Supabase is not configured.
 * RLS policies on each table ensure users can only delete their own rows.
 */

import { getClient } from "./client.js";

const ALL_TABLES = [
  "cards",
  "preferences",
  "reviews",
  "activity_sessions",
  "item_events",
] as const;

const PROGRESS_TABLES = ["cards", "reviews"] as const;

/**
 * Delete all rows belonging to the current user from every synced table.
 * Equivalent to a full remote wipe — user stays signed in.
 */
export async function clearUserData(): Promise<{ error: string | null }> {
  const supabase = getClient();
  if (!supabase) return { error: null };

  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (!userId) return { error: "Not signed in." };

  for (const table of ALL_TABLES) {
    const { error } = await supabase.from(table).delete().eq("user_id", userId);
    if (error) return { error: `Failed to clear ${table}: ${error.message}` };
  }
  return { error: null };
}

/**
 * Delete only cards and reviews for the current user — preserves
 * preferences, listen sessions, and challenge attempts.
 * Used by "Reset placement" so the user's settings and drill history survive.
 */
export async function clearUserProgress(): Promise<{ error: string | null }> {
  const supabase = getClient();
  if (!supabase) return { error: null };

  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (!userId) return { error: "Not signed in." };

  for (const table of PROGRESS_TABLES) {
    const { error } = await supabase.from(table).delete().eq("user_id", userId);
    if (error) return { error: `Failed to clear ${table}: ${error.message}` };
  }
  return { error: null };
}
